import { useCallback } from 'react';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { targetFromUrl } from 'legacyfrontend/utils/url';

export const useBannerNavigation = () => {
  const getPackageType = useCallback((urlPackage) => {
    if (!urlPackage || urlPackage.trim() === '') return null;
    
    try {
      const [pathname, queryString] = urlPackage.split('?');
      const searchParams = new URLSearchParams(queryString || '');
      
      if (searchParams.has('salidaId')) {
        const id = searchParams.get('id');
        const salidaId = searchParams.get('salidaId');
        
        if (id && salidaId) {
          return {
            type: 'internal',
            id,
            salidaId,
            isValid: true
          };
        }
      }
      
      if (pathname.includes('/paquetes')) {
        const id = searchParams.get('id');
        
        return {
          type: 'internal',
          id: id || null,
          salidaId: searchParams.get('salidaId') || null,
          isValid: !!id
        };
      }
      
      if (pathname.includes('/paquete')) {
        const pathParts = pathname.split('/');
        const packageId = pathParts[pathParts.length - 1];
        
        if (packageId && packageId.trim() !== '') {
          return {
            type: 'external',
            id: packageId,
            isValid: true
          };
        }
        
        return {
          type: 'external',
          id: null,
          isValid: false
        };
      }
      
      return null;
    } catch (error) {
      console.warn('Error parsing urlPackage:', urlPackage, error);
      return null;
    }
  }, []);

  const getNavigationUrl = useCallback((data) => {
    const { urlPackage, url, esConsulta, detalle, id, title } = data;
    
    if (urlPackage && urlPackage.trim() !== '') {
      if (urlPackage.startsWith('http')) {
        return urlPackage;
      }
      
      if (urlPackage.startsWith('/')) {
        return urlPackage;
      }
      
      return `/${urlPackage}`;
    }

    if (url) {
      return url;
    }

    if (esConsulta && id) {
      return `${ROUTE_KEYS.CONSULTA}/${id}`;
    }

    if (detalle && id) {
      return `${ROUTE_KEYS.CONSULTA}/${id}`;
    }

    if (id) {
      return `${ROUTE_KEYS.CONSULTA}/${id}`;
    }

    return ROUTE_KEYS.BUSCAR_X_PAQUETES;
  }, []);

  const getNavigationTarget = useCallback((data) => {
    const { urlPackage, url, esConsulta } = data;
    
    if (urlPackage && urlPackage.trim() !== '') {
      const packageInfo = getPackageType(urlPackage);
      
      if (urlPackage.startsWith('http')) {
        return '_blank';
      }
      
      if (packageInfo && packageInfo.type === 'internal' && packageInfo.isValid) {
        return '_self';
      }
      
      if (packageInfo && packageInfo.type === 'external' && packageInfo.isValid) {
        return '_blank';
      }
      
      return '_self';
    }

    if (url) {
      return targetFromUrl(url);
    }

    if (esConsulta) {
      return '_self';
    }

    return '_self';
  }, [getPackageType]);

  const getPackageInfo = useCallback((data) => {
    const { urlPackage } = data;
    
    if (!urlPackage || urlPackage.trim() === '') {
      return null;
    }
    
    return getPackageType(urlPackage);
  }, [getPackageType]);

  const getFallbackInfo = useCallback((data) => {
    const { urlPackage, url, esConsulta, detalle, id } = data;
    
    if (urlPackage && urlPackage.trim() !== '') {
      const packageInfo = getPackageType(urlPackage);
      if (!packageInfo) {
        return {
          reason: 'invalid_package_url',
          message: 'URL de paquete no válida o no reconocida',
          fallbackUsed: 'search_packages'
        };
      }
      if (!packageInfo.isValid) {
        return {
          reason: 'incomplete_package_data',
          message: `Datos incompletos para paquete ${packageInfo.type}`,
          fallbackUsed: 'search_packages'
        };
      }
      return null;
    }

    if (!url && !esConsulta && !detalle && !id) {
      return {
        reason: 'no_navigation_data',
        message: 'No hay información de navegación disponible',
        fallbackUsed: 'search_packages'
      };
    }

    if (id && !esConsulta && !detalle) {
      return {
        reason: 'id_without_context',
        message: 'ID disponible pero sin contexto de navegación',
        fallbackUsed: 'consulta_detail'
      };
    }

    return null;
  }, [getPackageType]);

  const getNavigationProps = useCallback((data) => {
    const href = getNavigationUrl(data);
    const target = getNavigationTarget(data);
    const packageInfo = getPackageInfo(data);
    const fallbackInfo = getFallbackInfo(data);
    
    return {
      href,
      target,
      packageInfo,
      fallbackInfo,
    };
  }, [getNavigationUrl, getNavigationTarget, getPackageInfo, getFallbackInfo]);

  return {
    getNavigationUrl,
    getNavigationTarget,
    getNavigationProps,
    getPackageInfo,
    getFallbackInfo,
  };
};

export default useBannerNavigation;